import { ContentfulClientApi, createClient } from 'contentful';
import { DEFAULT_HOST } from './constants';
import { PricingPlans, ProductKey, ProductKeyMap } from './models';
import { AddonProduct } from './models/addon-product';
import {
  mapPricingPlanContentEntryToPricingPlan,
  PricingPlanContent,
  ProductContentSkeleton
} from './product-catalog-client.types';
import * as Contentful from 'contentful';

export type ProductCatalogClientOptions = {
  readonly space: string;
  readonly environment: string;
  readonly accessToken: string;
  readonly host?: string;
};

export const SELECT_QUERY = [
  'fields.key',
  'fields.defaultStdPricingPlanMonthly',
  'fields.defaultStdPricingPlanYearly',
  'fields.defaultPremiumPricingPlanMonthly',
  'fields.defaultPremiumPricingPlanYearly',
  'fields.defaultStdPricingPlanYearly',
  'fields.defaultFreePricingPlanMonthly',
  'fields.defaultFreePricingPlanMonthly',
  'fields.defaultEnterprisePricingPlanMonthly',
  'fields.defaultEnterprisePricingPlanYearly'
];

export interface IProductCatalogClient {
  getPricing({
    productKeys
  }: GetPricingOptions): Promise<ProductKeyMap<PricingPlans>>;
  searchAddons({
    parentProductKey,
    query,
    limit
  }: SearchAddonsOptions): Promise<AddonProduct[]>;
}

export class ProductCatalogClient implements IProductCatalogClient {
  private readonly contentfulClient: ContentfulClientApi<undefined>;

  public constructor(options: ProductCatalogClientOptions) {
    this.contentfulClient = createClient({
      ...options,
      host: options.host || DEFAULT_HOST
    });
  }

  public async getPricing({
    productKeys
  }: GetPricingOptions): Promise<ProductKeyMap<PricingPlans>> {
    const { items } =
      await this.contentfulClient.getEntries<ProductContentSkeleton>({
        'content_type': 'product',
        //@ts-ignore
        'fields.key[in]': [
          ...new Set(productKeys.map(removeEditionSuffix)).values()
        ],
        'select': [
          'fields.key',
          'fields.defaultStdPricingPlanMonthly',
          'fields.defaultStdPricingPlanYearly',
          'fields.defaultPremiumPricingPlanMonthly',
          'fields.defaultPremiumPricingPlanYearly',
          'fields.defaultStdPricingPlanYearly',
          'fields.defaultFreePricingPlanMonthly',
          'fields.defaultFreePricingPlanMonthly',
          'fields.defaultEnterprisePricingPlanMonthly',
          'fields.defaultEnterprisePricingPlanYearly'
        ]
      });

    let ret: ProductKeyMap<PricingPlans> = {};
    for (const productKey of productKeys) {
      const editionlessKey = removeEditionSuffix(productKey);
      const entry = items.find(({ fields: { key } }) => key === editionlessKey);
      if (entry) {
        ret = {
          ...ret,
          [productKey]: getPricingPlansForEditionSuffixedKey(entry, productKey)
        };
      }
    }

    return ret;
  }

  public async searchAddons({
    parentProductKey,
    query,
    limit
  }: SearchAddonsOptions): Promise<AddonProduct[]> {
    const { items } = await this.contentfulClient.getEntries({
      'content_type': 'product',
      'select': [
        'fields.key',
        'fields.defaultStdPricingPlanMonthly',
        'fields.defaultStdPricingPlanYearly',
        'fields.defaultPremiumPricingPlanMonthly',
        'fields.defaultPremiumPricingPlanYearly',
        'fields.defaultStdPricingPlanYearly',
        'fields.defaultFreePricingPlanMonthly',
        'fields.defaultFreePricingPlanMonthly',
        'fields.defaultEnterprisePricingPlanMonthly',
        'fields.defaultEnterprisePricingPlanYearly',
        'fields.description'
      ],
      'fields.productType[match]': 'ADDON',
      'fields.description[match]': query,
      'fields.parentProductName[match]': parentProductKey,
      limit
    });

    const ret = items.map(({ fields: { description, key } }) => ({
      name: description,
      hamsProductKey: key || ''
    }));

    //@ts-ignore
    return ret;
  }
}

enum EDITION_SUFFIXES {
  FREE = '.free',
  STANDARD = '',
  PREMIUM = '.premium',
  ENTERPRISE = '.enterprise'
}

type EditionSuffix = { [P in keyof EDITION_SUFFIXES]: EDITION_SUFFIXES[P] };

const NON_STANDARD_EDITION_SUFFIXES = [
  EDITION_SUFFIXES.FREE,
  EDITION_SUFFIXES.PREMIUM,
  EDITION_SUFFIXES.ENTERPRISE
];

export const removeEditionSuffix = (productKey: string) =>
  productKey.replace(
    new RegExp(
      NON_STANDARD_EDITION_SUFFIXES.map((suffix) => `\\${suffix}`).join('|') +
        '$'
    ),
    ''
  );

const getPricingPlansForEditionSuffixedKey = (
  productContent: Contentful.Entry<ProductContentSkeleton>,
  editionSuffixedProductKey: string
): PricingPlans => {
  const matchedEditionSuffix: EditionSuffix =
    NON_STANDARD_EDITION_SUFFIXES.reduce(
      (matchingEditionSuffix, editionSuffix) =>
        editionSuffixedProductKey.endsWith(editionSuffix)
          ? editionSuffix
          : matchingEditionSuffix,
      EDITION_SUFFIXES.STANDARD
    );

  let monthly: Contentful.EntrySkeletonType<PricingPlanContent> | null = null;
  let annual: Contentful.EntrySkeletonType<PricingPlanContent> | null = null;
  switch (matchedEditionSuffix) {
    case EDITION_SUFFIXES.FREE: {
      const { defaultFreePricingPlanMonthly, defaultFreePricingPlanYearly } =
        productContent.fields;

      monthly =
        defaultFreePricingPlanMonthly as unknown as Contentful.EntrySkeletonType<PricingPlanContent>;
      annual =
        defaultFreePricingPlanYearly as unknown as Contentful.EntrySkeletonType<PricingPlanContent>;
      break;
    }

    case EDITION_SUFFIXES.STANDARD: {
      const { defaultStdPricingPlanMonthly, defaultStdPricingPlanYearly } =
        productContent.fields;
      monthly =
        defaultStdPricingPlanMonthly as unknown as Contentful.EntrySkeletonType<PricingPlanContent>;
      annual =
        defaultStdPricingPlanYearly as unknown as Contentful.EntrySkeletonType<PricingPlanContent>;
      break;
    }

    case EDITION_SUFFIXES.PREMIUM: {
      const {
        defaultPremiumPricingPlanMonthly,
        defaultPremiumPricingPlanYearly
      } = productContent.fields;

      monthly =
        defaultPremiumPricingPlanMonthly as unknown as Contentful.EntrySkeletonType<PricingPlanContent>;
      annual =
        defaultPremiumPricingPlanYearly as unknown as Contentful.EntrySkeletonType<PricingPlanContent>;
      break;
    }

    case EDITION_SUFFIXES.ENTERPRISE: {
      const {
        defaultEnterprisePricingPlanMonthly,
        defaultEnterprisePricingPlanYearly
      } = productContent.fields;

      monthly =
        defaultEnterprisePricingPlanMonthly as unknown as Contentful.EntrySkeletonType<PricingPlanContent>;
      annual =
        defaultEnterprisePricingPlanYearly as unknown as Contentful.EntrySkeletonType<PricingPlanContent>;
      break;
    }
  }

  return {
    monthly: mapPricingPlanContentEntryToPricingPlan(monthly),
    annual: mapPricingPlanContentEntryToPricingPlan(annual)
  };
};

export type GetPricingOptions = {
  readonly productKeys: ReadonlyArray<ProductKey>;
};

export type SearchAddonsOptions = {
  readonly parentProductKey: ProductKey;
  readonly query: string;
  readonly limit?: number;
};
